import { Dialog } from '@kobalte/core/dialog'
import { Show, onCleanup, type JSX } from 'solid-js'
import type { AlertScope } from 'mormaii-web-presentation/main'

import Css from './dialog.module.scss'
import { Button } from '../filters'
import { Icon } from '../icons'

/**
 * O dialogo do painel.
 *
 * Sobre o `Dialog` do Kobalte, que traz o que um dialogo precisa e que quase nunca
 * se escreve a mao direito: foco presos dentro, Escape, clique fora e `aria-modal`.
 *
 * Um dialogo daqui esta SEMPRE aberto: quem decide se ele existe e o scope - o place
 * o criou -, e nao um estado interno. Fechar, portanto, nao e esconder: e avisar
 * quem o abriu (`onClose`), que desfaz o place e com isso apaga o scope.
 *
 * DEVOLVER O FOCO a quem abriu e daqui (19/10/2026). O dialogo modal do Kobalte
 * devolve ao seu GATILHO (`Dialog.Trigger`), e o painel nao usa gatilho: quem abre e o
 * place. Sem isto o foco caia na pagina, e quem usa o teclado tinha de voltar do
 * inicio ate a linha em que estava. A devolucao acontece no proprio gancho do Kobalte
 * (`onCloseAutoFocus`), no lugar da dele.
 *
 * Quem abriu forma uma CADEIA: um aviso que nasce sobre a confirmacao ("Acesso
 * bloqueado" depois de "Confirmar") tem o foco dentro dela, e herda quem abriu a
 * confirmacao. O foco so volta quando nao sobra dialogo nenhum aberto.
 */

/** Os dialogos abertos agora, cada um com quem o abriu. */
const openDialogs = new Set<{ opener?: HTMLElement; content?: HTMLElement }>()

/**
 * Quem abriu o ultimo dialogo que saiu, enquanto o foco ainda nao voltou. O dialogo
 * que nasce nesse intervalo (outro place trocou de lugar com o anterior) o herda.
 */
let pendingOpener: HTMLElement | undefined

function openerFor(active: Element | null): HTMLElement | undefined {
    if (!(active instanceof HTMLElement) || active === document.body) {
        // Sem foco nenhum - o botao que confirmou ficou desabilitado enquanto a acao rodava,
        // e botao desabilitado perde o foco. Herda o do dialogo aberto mais recente, ou o
        // do que acabou de sair.
        const latest = [...openDialogs].at(-1)
        return latest ? latest.opener : pendingOpener
    }
    // O foco esta dentro de outro dialogo: herda quem abriu aquele.
    for (const dialog of openDialogs) {
        if (dialog.content?.contains(active)) return dialog.opener
    }
    return active
}

export function Modal(props: {
    title: string
    children: JSX.Element
    footer?: JSX.Element
    onClose: () => void
    /** Larga - o formulario de empresa tem sete campos em duas colunas. */
    wide?: boolean
}): JSX.Element {
    // Quem tinha o foco quando o dialogo nasceu - em geral, o botao que o abriu.
    const self: { opener?: HTMLElement; content?: HTMLElement } = { opener: openerFor(document.activeElement) }
    openDialogs.add(self)

    onCleanup(() => {
        openDialogs.delete(self)
        pendingOpener = self.opener
    })

    /*
     * O Kobalte chama isto depois de o dialogo sair do DOM. So devolve se nao sobrou
     * dialogo aberto (o que nasceu no lugar deste herdou quem o abriu), se quem abriu
     * ainda esta na tela e se ninguem pos o foco em outro lugar no meio tempo.
     */
    const restoreFocus = (event: Event) => {
        event.preventDefault()
        if (openDialogs.size > 0) return
        pendingOpener = undefined
        const opener = self.opener
        if (!opener?.isConnected) return
        const active = document.activeElement
        if (active && active !== document.body && active.isConnected) return
        opener.focus({ preventScroll: true })
    }

    return (
        <Dialog open onOpenChange={(open) => !open && props.onClose()}>
            <Dialog.Portal>
                <Dialog.Overlay class={Css.backdrop} />

                <div class={Css.center}>
                    <Dialog.Content
                        ref={(element) => (self.content = element)}
                        class={Css.box}
                        classList={{ [Css.wide]: props.wide }}
                        onCloseAutoFocus={restoreFocus}
                    >
                        <header class={Css.header}>
                            <Dialog.Title class={Css.title}>{props.title}</Dialog.Title>
                            <Dialog.CloseButton aria-label="Fechar" class={Css.close}>
                                {/*
                                    O tamanho do icone vem do SCSS (`.fechar svg`), e
                                    nao de uma classe aqui: ele e do botao, e nao
                                    deste desenho em particular.
                                */}
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M18 6 6 18M6 6l12 12" stroke-linecap="round" />
                                </svg>
                            </Dialog.CloseButton>
                        </header>

                        <div class={Css.body}>{props.children}</div>

                        <Show when={props.footer}>{(footer) => <footer class={Css.footer}>{footer()}</footer>}</Show>
                    </Dialog.Content>
                </div>
            </Dialog.Portal>
        </Dialog>
    )
}

/**
 * O aviso, e a pergunta.
 *
 * Um dialogo so para os dois casos, porque a diferenca e um botao: sem
 * `confirmText` ele apenas informa e tem "Ok"; com ele, ganha o par
 * confirmar/cancelar. Fechar por Escape, por clique fora, pelo X ou pelo Cancelar e
 * a MESMA coisa - e nenhuma delas confirma.
 */
export function AlertView(props: { scope: AlertScope }): JSX.Element {
    const confirms = () => !!props.scope.confirmText

    return (
        <Modal
            title={props.scope.title}
            onClose={() => void props.scope.onClose()}
            footer={
                <>
                    <Show when={confirms() && props.scope.cancelText}>
                        {(label) => (
                            <Button variant="ghost" onClick={() => void props.scope.onClose()}>
                                {label()}
                            </Button>
                        )}
                    </Show>
                    <Button
                        variant={props.scope.severity === 'error' ? 'danger' : 'primary'}
                        onClick={() => (confirms() ? void props.scope.onConfirm() : void props.scope.onClose())}
                    >
                        {props.scope.confirmText ?? 'Ok'}
                    </Button>
                </>
            }
        >
            <div class={Css.notice}>
                <Show when={props.scope.icon}>
                    <span class={Css.noticeIcon}>
                        <Icon name={props.scope.icon} size={18} />
                    </span>
                </Show>
                <p class={Css.noticeText}>{props.scope.message}</p>
            </div>
        </Modal>
    )
}
